// src/Dashboard/LearnerDashboard.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getChurches } from "../services/api"; // ✅ resolve church name from org structure API

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchJSON(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const json = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json?.data ?? json;
}

function pickDisplayName(apiUser = {}, clerkUser) {
  const join = (a, b) => [a, b].filter(Boolean).join(" ").trim();
  const candidates = [
    apiUser.full_name,
    apiUser.fullName,
    apiUser.name,
    join(apiUser.first_name, apiUser.last_name),
    join(apiUser.firstName, apiUser.lastName),
    clerkUser?.fullName,
    join(clerkUser?.firstName, clerkUser?.lastName),
  ]
    .map((v) => (typeof v === "string" ? v.trim() : v))
    .filter((v) => v && v !== "undefined" && v !== "undefined undefined");

  return candidates[0] || "Learner";
}

function formatDateShort(value) {
  try {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return null;
  }
}

/** Local fallback courses (normalized to the dashboard shape) */
const FALLBACK_COURSES = [
  {
    slug: "discover",
    title: "Discover",
    description: "A 26-lesson journey through Bible foundations and prophecy.",
    total_lessons: 26,
    status: "available",
  },
  {
    slug: "ugunduzi",
    title: "Ugunduzi",
    description: "Discover Swahili lessons — 26 topics of faith and discovery.",
    total_lessons: 26,
    status: "available",
  },
  {
    slug: "daniel-revelation",
    title: "Daniel & Revelation",
    description: "Prophetic study of Daniel & Revelation — 21 deep lessons.",
    total_lessons: 21,
    status: "available",
  },
  {
    slug: "health",
    title: "Health",
    description: "Bible-based health and wellness education — 20 lessons.",
    total_lessons: 20,
    status: "available",
  },
  {
    // Keep slug as focus-family (your LessonViewer handles /lessons/family/ loading)
    slug: "focus-family",
    title: "Focus on the Family",
    description: "Faith-based family life & relationships — 16 lessons.",
    total_lessons: 16,
    status: "available",
  },
];

export default function LearnerDashboard() {
  const { getToken } = useAuth();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [me, setMe] = useState(null); // {name, church, instructor}
  const [courses, setCourses] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [announcements, setAnnouncements] = useState([]);

  // ✅ churchId -> churchName cache (resolved from org API via /api/hierarchy/church)
  const [churchNameMap, setChurchNameMap] = useState(null);

  const ensureChurchMap = useCallback(async () => {
    if (churchNameMap) return churchNameMap;
    const list = await getChurches().catch(() => []);
    const m = new Map();
    (Array.isArray(list) ? list : []).forEach((c) => {
      m.set(String(c.id), c.name || "");
    });
    setChurchNameMap(m);
    return m;
  }, [churchNameMap]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const token = await getToken().catch(() => null);

        // 1) Current user (auto-created by backend if missing)
        const meRow = await fetchJSON("/api/users/me", { token }).catch(() => ({}));

        // Resolve church name (org-structure API)
        const churchId = meRow?.church_id ?? meRow?.scope?.churchId ?? null;
        let churchDisplay = meRow?.church_name ?? meRow?.church ?? null;

        // If backend didn't provide name but we have ID, resolve via cached hierarchy list
        if (!churchDisplay && churchId) {
          const m = await ensureChurchMap();
          churchDisplay = m.get(String(churchId)) || null;
        }

        // No-church path (for not SDA users)
        if (!churchDisplay) churchDisplay = "No church / Not SDA";

        const meData = {
          name: pickDisplayName(meRow, user),
          church: churchDisplay,
          instructor: meRow?.instructor_name ?? meRow?.instructor ?? "—",
        };

        // 2) Courses: try API, else fallback. Normalize to {slug,title,description,total_lessons,status}
        let normalized = [];
        try {
          const list = await fetchJSON("/api/content/courses", { token });
          const apiCourses = Array.isArray(list) ? list : [];
          const mapped = apiCourses.map((c) => ({
            slug: c.slug || c.id,
            title: c.title,
            description: c.description || "",
            status: c.status || (c.is_published ? "published" : "available"),
            total_lessons: Number(c.total_lessons || c.lessons || c.lessonCount || 0),
          }));
          normalized = mapped.filter((c) => typeof c.slug === "string" && c.slug.trim().length > 0);
        } catch {
          normalized = [];
        }

        // Detect if API already has a family course (by title)
        const apiHasFamily = normalized.some((c) => c.title && c.title.toLowerCase().includes("family"));

        // Merge with fallback if API empty or incomplete (dedupe by slug, prefer API)
        const bySlug = new Map(normalized.map((c) => [c.slug, c]));
        for (const f of FALLBACK_COURSES) {
          if (f.slug === "focus-family" && apiHasFamily) continue;
          if (!bySlug.has(f.slug)) bySlug.set(f.slug, f);
        }
        const mergedCourses = Array.from(bySlug.values());

        // 3) Announcements
        const anns = await fetchJSON("/api/status/announcements", { token }).catch(() => null);
        const mappedAnns =
          Array.isArray(anns) && anns.length
            ? anns.map((a) => ({
                id:
                  a.id ||
                  a.uid ||
                  `${a.level || "union"}-${a.target_id || "all"}-${a.created_at || a.publish_at || ""}`,
                title: a.title || "Announcement",
                body: a.body || "",
                createdAt: a.created_at || a.publish_at || null,
              }))
            : [
                { id: 1, title: "Welcome!", body: "Keep learning—your progress is saved automatically." },
                { id: 2, title: "Tip", body: "Completed a lesson? Try the quiz to solidify your understanding." },
              ];

        // 4) Progress per course
        const progressEntries = await Promise.all(
          mergedCourses.map(async (c) => {
            if (!c.slug) return [c.slug, { completed: [], completionPct: 0, nextLesson: 1 }];
            try {
              const p = await fetchJSON(`/api/progress/me/${encodeURIComponent(c.slug)}`, { token });

              const completed =
                Array.isArray(p?.lessonsCompleted)
                  ? p.lessonsCompleted
                  : Array.isArray(p?.data?.lessonsCompleted)
                  ? p.data.lessonsCompleted
                  : Array.isArray(p?.completed)
                  ? p.completed
                  : Array.isArray(p?.data?.completed)
                  ? p.data.completed
                  : [];

              const lessons = Number(c.total_lessons || 0);
              const nextLesson =
                typeof p?.nextLesson === "number"
                  ? p.nextLesson
                  : typeof p?.data?.nextLesson === "number"
                  ? p.data.nextLesson
                  : completed.length
                  ? Math.min(lessons || 1, Math.max(...completed) + 1)
                  : 1;

              const completionPct = lessons ? Math.min(100, Math.round((completed.length / lessons) * 100)) : 0;

              return [c.slug, { completed, completionPct, nextLesson }];
            } catch {
              return [c.slug, { completed: [], completionPct: 0, nextLesson: 1 }];
            }
          })
        );
        const pMap = Object.fromEntries(progressEntries);

        if (!alive) return;
        setMe(meData);
        setCourses(mergedCourses);
        setAnnouncements(mappedAnns);
        setProgressMap(pMap);
      } catch (e) {
        console.error("LearnerDashboard load error:", e);
        if (alive) setErr(e.message || "Failed to load dashboard");
        if (alive) setCourses(FALLBACK_COURSES);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [getToken, user, ensureChurchMap]);

  const availableCourses = useMemo(() => courses, [courses]);

  // ✅ Real overall stats
  const overallStats = useMemo(() => {
    const list = availableCourses || [];
    if (!list.length) {
      return { totalCourses: 0, totalLessons: 0, completedLessons: 0, overallPct: 0 };
    }

    let totalLessons = 0;
    let completedLessons = 0;

    for (const c of list) {
      const lessons = Number(c.total_lessons || 0);
      totalLessons += lessons;
      const p = progressMap[c.slug];
      const completed = Array.isArray(p?.completed) ? p.completed.length : 0;
      completedLessons += Math.min(completed, lessons);
    }

    const overallPct = totalLessons ? Math.min(100, Math.round((completedLessons / totalLessons) * 100)) : 0;

    return {
      totalCourses: list.length,
      totalLessons,
      completedLessons,
      overallPct,
    };
  }, [availableCourses, progressMap]);

  // ✅ Find the best “continue” label (button still goes to /courses as requested)
  const continueTarget = useMemo(() => {
    const list = (availableCourses || []).slice();
    if (!list.length) return { label: "Continue learning", to: "/courses" };

    const withProgress = list.map((c) => {
      const p = progressMap[c.slug] || { completionPct: 0, nextLesson: 1 };
      return { course: c, pct: Number(p.completionPct || 0) };
    });

    const started = withProgress.filter((x) => x.pct > 0 && x.pct < 100);
    const notStarted = withProgress.filter((x) => x.pct === 0);
    const finished = withProgress.filter((x) => x.pct >= 100);

    const pick = (arr) => arr.sort((a, b) => a.pct - b.pct)[0];
    const chosen = pick(started) || pick(notStarted) || pick(finished) || withProgress[0];

    return {
      label: chosen?.course?.title ? `Continue: ${chosen.course.title}` : "Continue learning",
      to: "/courses",
    };
  }, [availableCourses, progressMap]);

  return (
    // ✅ overflow-x-hidden prevents any accidental horizontal spill on mobile
    <div className="min-h-screen flex flex-col overflow-x-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <Navbar />

      <main className="flex-1">
        <div className="pt-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-12">
          {/* HERO */}
          <header className="mb-10">
            <div className="relative overflow-hidden rounded-3xl border border-orange-100 bg-white/80 backdrop-blur-xl shadow-xl dark:bg-slate-950/60 dark:border-slate-800">
              <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-200/60 blur-3xl dark:bg-orange-500/20" />
              <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-200/60 blur-3xl dark:bg-amber-500/20" />

              <div className="relative p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  {/* ✅ min-w-0 lets children truncate instead of forcing overflow */}
                  <div className="min-w-0">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                      {loading ? "Loading your dashboard…" : "Welcome back 👋"}
                    </h1>
                    <p className="text-slate-600 dark:text-slate-300 mt-1">
                      Track your progress and continue learning in the Virtual School.
                    </p>

                    {/* Meta chips */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="max-w-full inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-semibold text-orange-700 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-200">
                        Overall Progress: <span className="font-extrabold">{overallStats.overallPct}%</span>
                      </span>

                      <span className="max-w-full inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
                        {overallStats.completedLessons}/{overallStats.totalLessons} lessons
                      </span>

                      {/* ✅ wrap + break-words to avoid long church names overflowing */}
                      <span className="max-w-full inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
                        <span className="shrink-0">Church:</span>
                        <span className="font-extrabold break-words min-w-0">{me?.church || "—"}</span>
                      </span>

                      <span className="max-w-full inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
                        <span className="shrink-0">Instructor:</span>
                        <span className="font-extrabold break-words min-w-0">{me?.instructor || "—"}</span>
                      </span>
                    </div>
                  </div>

                  {/* ✅ CTA buttons become full width on mobile */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                    <Link
                      to={continueTarget.to}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-bold shadow-lg hover:opacity-95 transition"
                      title="Continue learning"
                    >
                      {/* ✅ avoid overflow for long titles */}
                      <span className="truncate max-w-[18rem] sm:max-w-none">{continueTarget.label}</span>
                      <span aria-hidden>→</span>
                    </Link>

                    <Link
                      to="/courses"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-bold text-slate-800 hover:bg-slate-50 transition dark:bg-slate-900 dark:text-white dark:border-slate-700 dark:hover:bg-slate-800"
                      title="Browse all courses"
                    >
                      Explore Courses
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {!!err && (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200 break-words">
              {err}
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main */}
            <div className="lg:col-span-2 space-y-8 min-w-0">
              {/* Announcements */}
              <section className="rounded-3xl border border-slate-200 bg-white shadow-lg p-6 dark:bg-slate-950/60 dark:border-slate-800 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-6 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white truncate">
                    Announcements
                  </h2>
                  <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                    {announcements.length} new
                  </span>
                </div>

                {/* ✅ scroll inside on mobile if many announcements; prevents page “spill” */}
                <div className="space-y-4 max-h-[65vh] overflow-auto overscroll-contain pr-1">
                  {announcements.map((a) => {
                    const when = formatDateShort(a.createdAt);
                    return (
                      <div
                        key={a.id || a.title}
                        className="rounded-2xl border border-orange-100 bg-gradient-to-r from-orange-50 to-white p-4 dark:from-orange-500/10 dark:to-slate-900 dark:border-orange-500/20 min-w-0"
                      >
                        <h3 className="font-extrabold text-orange-700 dark:text-orange-200 break-words">
                          {a.title}
                        </h3>
                        {/* ✅ break-words prevents long unbroken strings (links, codes) from overflowing */}
                        <p className="text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap break-words">
                          {a.body}
                        </p>
                        {when && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2 break-words">
                            {when}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {!announcements.length && (
                    <div className="text-slate-500 dark:text-slate-400">No announcements yet.</div>
                  )}
                </div>
              </section>

              {/* Courses Preview */}
              <section className="min-w-0">
                <div className="flex items-center justify-between gap-3 mb-5 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white truncate">
                    Available Courses
                  </h2>
                  <Link
                    to="/courses"
                    className="text-sm font-bold text-orange-700 hover:text-orange-800 dark:text-orange-200 dark:hover:text-orange-100 shrink-0"
                  >
                    View all →
                  </Link>
                </div>

                {loading ? (
                  <div className="text-slate-600 dark:text-slate-300">Loading courses…</div>
                ) : availableCourses.length ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {availableCourses
                      .slice()
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map((course) => {
                        const p = progressMap[course.slug] || { completionPct: 0, nextLesson: 1 };
                        const safeCompletion = Math.min(100, Math.max(0, p.completionPct || 0));
                        const totalLessons = course.total_lessons || 1;
                        const safeNext = Math.min(p.nextLesson || 1, totalLessons);

                        return (
                          <Link
                            key={course.slug}
                            to={`/courses/${encodeURIComponent(course.slug)}`}
                            className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all dark:bg-slate-950/60 dark:border-slate-800 min-w-0"
                          >
                            <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
                              <div className="rounded-2xl bg-gradient-to-br from-orange-600 to-amber-600 p-3 shadow-md shrink-0">
                                <svg
                                  className="w-6 h-6 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18"
                                  />
                                </svg>
                              </div>

                              <div className="text-sm font-bold text-slate-500 dark:text-slate-400 shrink-0">
                                {safeCompletion}% complete
                              </div>
                            </div>

                            <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 group-hover:text-orange-700 transition-colors dark:text-white dark:group-hover:text-orange-200 break-words">
                              {course.title}
                            </h3>

                            <p className="text-slate-600 dark:text-slate-300 mt-2 leading-relaxed break-words">
                              {course.description}
                            </p>

                            {/* Progress bar */}
                            <div className="mt-4">
                              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden dark:bg-slate-800">
                                <div
                                  className="h-2 bg-gradient-to-r from-orange-600 to-amber-600 rounded-full"
                                  style={{ width: `${safeCompletion}%` }}
                                />
                              </div>
                            </div>

                            <div className="mt-4 flex items-center justify-between text-sm gap-3 min-w-0">
                              <span className="text-slate-500 dark:text-slate-400 shrink-0">
                                Lessons: {course.total_lessons}
                              </span>
                              <span className="font-bold text-orange-700 dark:text-orange-200 break-words text-right min-w-0">
                                Continue at lesson {safeNext} →
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-slate-600 dark:text-slate-300">No courses available yet.</div>
                )}
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-8 min-w-0">
              {/* Quick Stats */}
              <section className="rounded-3xl bg-gradient-to-br from-orange-600 to-amber-600 shadow-lg p-6 text-white min-w-0">
                <h3 className="text-lg font-extrabold mb-4 opacity-95">Quick Stats</h3>

                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-3 min-w-0">
                    <span className="text-white/90 truncate">Available Courses</span>
                    <span className="text-2xl font-extrabold shrink-0">{overallStats.totalCourses}</span>
                  </div>

                  <div className="flex justify-between items-center gap-3 min-w-0">
                    <span className="text-white/90 truncate">Overall Progress</span>
                    <span className="text-2xl font-extrabold shrink-0">{overallStats.overallPct}%</span>
                  </div>

                  <div className="flex justify-between items-center gap-3 min-w-0">
                    <span className="text-white/90 truncate">Announcements</span>
                    <span className="text-2xl font-extrabold shrink-0">{announcements.length}</span>
                  </div>
                </div>
              </section>

              {/* What next */}
              <section className="rounded-3xl border border-slate-200 bg-white shadow-lg p-6 dark:bg-slate-950/60 dark:border-slate-800 min-w-0">
                <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">What’s next</h3>
                <p className="text-slate-600 dark:text-slate-300 mt-2 break-words">
                  Keep a steady routine: finish a lesson, take the quiz, and track your progress.
                </p>

                <div className="mt-4 flex flex-col gap-3">
                  <Link
                    to="/courses"
                    className="w-full inline-flex items-center justify-center rounded-2xl bg-slate-900 text-white px-4 py-3 font-bold hover:opacity-90 transition dark:bg-white dark:text-slate-900"
                  >
                    Continue learning
                  </Link>

                
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* ✅ Shared footer component (always visible) */}
      <Footer />
    </div>
  );
}
